import { useCallback } from 'react';

import { AlertStatus, Link } from '@make-software/cspr-ui';
import { Alert } from '@block-explorer/ui';
import { Trans, useTranslation } from 'react-i18next';

import { MINIMUM_BALANCE } from '../../domain';
import { DELEGATE_STAKE_PATH, UNDELEGATE_STAKE_PATH } from '../../router/paths';
import { motesToCSPR } from '../../utils/currency';
import { ValidateWalletConnected } from './validate-wallet-connected';
import { getRouteDetails, useRouteMatchPath } from '../../router/utils';
import { ValidationWalletDataType } from '../../services/hooks/validate-wallet-connection';
import styled from 'styled-components';
import { useWalletService } from '../../services';
import { useGetCasperNetwork } from '../../services/casper-network-context.tsx';

export interface WalletConnectionValidationMessagesProps {
  validationWalletData: ValidationWalletDataType;
  walletServiceDisconnect: () => void;
  minimumBalance?: string;
  children?: any;
}

const StyledLink = styled(Link)(({ theme }) => ({
  fontWeight: theme.typography.fontWeight.semiBold,
}));

export function WalletConnectionValidationMessages({
  validationWalletData,
  walletServiceDisconnect,
  minimumBalance = MINIMUM_BALANCE,
  children,
}: WalletConnectionValidationMessagesProps) {
  const { t } = useTranslation();
  const { networkMajorVersion } = useGetCasperNetwork();
  const { path } = getRouteDetails(useRouteMatchPath(), t, networkMajorVersion);
  const { buyCSPR } = useWalletService();

  const {
    isInsufficentBalance,
    readOnlyIsConnected,
    ledgerDelegationsDisabled,
  } = validationWalletData;

  const handleSignOutClick = useCallback(() => {
    walletServiceDisconnect();
  }, []);

  const handleBuyCspr = () => {
    buyCSPR();
  };

  const isDelegation =
    path === DELEGATE_STAKE_PATH || path === UNDELEGATE_STAKE_PATH;

  if (ledgerDelegationsDisabled && isDelegation) {
    return (
      <Alert
        status={AlertStatus.Error}
        title={<Trans t={t}>Coming soon</Trans>}
        message={
          <>
            Ledger support for Delegation/Undelegation is disabled.
            <br />
            <Link color="primaryRed" onClick={handleSignOutClick}>
              Sign out
            </Link>{' '}
            and connect with Casper Wallet to continue.
          </>
        }
      />
    );
  }

  if (readOnlyIsConnected) {
    return (
      <Alert
        status={AlertStatus.Error}
        title={
          <Trans t={t}>You're signed in with a read-only web account.</Trans>
        }
        message={
          <Trans t={t}>
            <Link color="primaryRed" onClick={handleSignOutClick}>
              Sign out
            </Link>{' '}
            and connect a compatible wallet like Casper Wallet or Ledger to
            continue.
          </Trans>
        }
      />
    );
  }

  return (
    <ValidateWalletConnected>
      {isInsufficentBalance ? (
        <Alert
          status={AlertStatus.Error}
          title={<Trans t={t}>Insufficient balance.</Trans>}
          message={
            <>
              <Trans t={t}>
                System requires {motesToCSPR(minimumBalance)} CSPR minimum
                balance.{' '}
              </Trans>
              {config.with_with_buy_cspr_button && (
                <Trans t={t}>
                  You can{' '}
                  <StyledLink color={'primaryBlue'} onClick={handleBuyCspr}>
                    buy CSPR
                  </StyledLink>{' '}
                  to unlock access to this feature.
                </Trans>
              )}
            </>
          }
        />
      ) : (
        children
      )}
    </ValidateWalletConnected>
  );
}
